/**
 * ProjectWelcomeModal Component
 * Mandatory onboarding modal to ensure a project is active before starting work.
 * Prevents data loss by enforcing project persistence context.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useProject } from '../hooks/useProject';
import { supabase } from '../services/supabaseClient';
import {
    FolderOpenIcon,
    PlusIcon,
    FolderIcon,
    KeyIcon,
} from './icons';

interface ProjectWelcomeModalProps {
    onOpenSettings?: () => void;
}

export const ProjectWelcomeModal: React.FC<ProjectWelcomeModalProps> = ({ onOpenSettings }) => {
    const {
        project,
        projects,
        createProject,
        openProject,
        importProject,
        deleteProject,
    } = useProject();

    const [newProjectName, setNewProjectName] = useState('');
    const [showNewProject, setShowNewProject] = useState(false);
    const [step, setStep] = useState(1); // 1=Name, 2=Storage
    const [driveConnected, setDriveConnected] = useState(false);
    const [driveFolders, setDriveFolders] = useState<{ id: string, name: string }[]>([]);
    const [selectedDriveFolder, setSelectedDriveFolder] = useState<string | null>(null);
    const [localPath, setLocalPath] = useState('');
    const [userId, setUserId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Get user ID for Drive auth
    useEffect(() => {
        supabase?.auth.getSession().then(({ data }) => {
            setUserId(data.session?.user?.id || null);
        });
    }, []);

    // Initial check for Drive connection
    React.useEffect(() => {
        if (showNewProject) {
            import('../services/googleDriveClient').then(async ({ isDriveConnected, listDriveFolders }) => {
                const connected = await isDriveConnected();
                setDriveConnected(connected);
                if (connected) {
                    const folders = await listDriveFolders();
                    setDriveFolders(folders);
                }
            });
        }
    }, [showNewProject]);

    // If a project is already active, do not show this modal
    if (project) return null;

    const handleNextStep = () => {
        if (newProjectName.trim()) {
            setStep(2);
        }
    };

    const handleCreate = async () => {
        if (newProjectName.trim()) {
            let folderId = selectedDriveFolder;
            let folderName = driveFolders.find(f => f.id === selectedDriveFolder)?.name;

            // If Drive connected but no folder selected (or "Create new" selected), create specific folder
            if (driveConnected && !folderId) {
                try {
                    // Import dynamically to avoid loading if not needed
                    const { createDriveFolder } = await import('../services/googleDriveClient');
                    const newFolder = await createDriveFolder(newProjectName.trim());
                    if (newFolder) {
                        folderId = newFolder.id;
                        folderName = newFolder.name;
                    }
                } catch (e) {
                    console.error('Failed to create Drive folder:', e);
                }
            }

            createProject(newProjectName.trim(), {
                driveFolderId: folderId || undefined,
                driveFolderName: folderName || undefined,
                localPath: localPath || undefined,
                storageMode: folderId ? 'hybrid' : (driveConnected ? 'hybrid' : 'local')
            });

            setNewProjectName('');
            setShowNewProject(false);
            setStep(1);
        }
    };

    const handleOpen = (id: string) => {
        openProject(id);
    };

    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            await importProject(file);
        }
        e.target.value = '';
    };

    const handleDelete = (id: string, name: string) => {
        if (confirm(`Êtes-vous sûr de vouloir supprimer le projet "${name}"?`)) {
            deleteProject(id);
        }
    };

    const handleSelectLocalFolder = async () => {
        try {
            // Use File System Access API if available
            if ('showDirectoryPicker' in window) {
                const handle = await (window as any).showDirectoryPicker();
                setLocalPath(handle.name); // Store name/path reference
            } else {
                alert("Votre navigateur ne supporte pas la sélection de dossiers. Entrez le chemin manuellement.");
            }
        } catch (e) {
            console.warn('Folder selection cancelled', e);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
            <div className="bg-gray-900 rounded-2xl w-full max-w-4xl h-[80vh] flex overflow-hidden border border-gray-700 shadow-2xl relative">

                {/* Decoration Canvas / Left Side */}
                <div className="w-1/3 bg-gradient-to-br from-indigo-900 via-purple-900 to-gray-900 p-8 flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

                    <div className="relative z-10">
                        <h1 className="text-4xl font-bold text-white mb-2">Studio Jenial</h1>
                        <p className="text-indigo-200 text-lg">Votre assistant de création vidéo augmenté.</p>
                    </div>

                    <div className="relative z-10">
                        <p className="text-gray-300 text-sm mb-6">
                            Pour garantir la sauvegarde de votre travail, configurez votre espace de travail.
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => setShowNewProject(true)}
                                className="flex items-center justify-center gap-2 px-6 py-4 bg-white text-indigo-900 font-bold rounded-xl hover:bg-indigo-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                            >
                                <PlusIcon className="w-6 h-6" />
                                Nouveau Projet
                            </button>
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center justify-center gap-2 px-6 py-4 bg-indigo-800/50 text-white font-semibold rounded-xl hover:bg-indigo-700/50 border border-indigo-500/30 transition-all"
                            >
                                <FolderOpenIcon className="w-6 h-6" />
                                Importer un fichier .jenial
                            </button>
                            {onOpenSettings && (
                                <button
                                    onClick={onOpenSettings}
                                    className="flex items-center justify-center gap-2 px-6 py-3 text-gray-400 hover:text-white font-medium rounded-xl hover:bg-gray-800/50 border border-gray-700/50 transition-all text-sm"
                                >
                                    <KeyIcon className="w-5 h-5" />
                                    Configurer les clés API
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Side - Project List or Form */}
                <div className="w-2/3 bg-gray-900 flex flex-col">
                    <div className="p-8 border-b border-gray-800">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                            <FolderIcon className="w-7 h-7 text-gray-500" />
                            {showNewProject ? 'Nouveau Projet' : 'Projets Récents'}
                        </h2>
                    </div>

                    <div className="flex-1 overflow-y-auto p-8">
                        {/* New Project Workflow */}
                        {showNewProject ? (
                            <div className="bg-indigo-900/10 border border-indigo-500/30 p-8 rounded-2xl animate-in fade-in slide-in-from-right-8">
                                {/* Step Indicator */}
                                <div className="flex items-center gap-4 mb-8 text-sm">
                                    <div className={`flex items-center gap-2 ${step >= 1 ? 'text-white font-bold' : 'text-gray-500'}`}>
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-indigo-600' : 'bg-gray-700'}`}>1</div>
                                        Nom
                                    </div>
                                    <div className="w-8 h-px bg-gray-700"></div>
                                    <div className={`flex items-center gap-2 ${step >= 2 ? 'text-white font-bold' : 'text-gray-500'}`}>
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-indigo-600' : 'bg-gray-700'}`}>2</div>
                                        Stockage
                                    </div>
                                </div>

                                {step === 1 && (
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-indigo-300 text-sm font-bold mb-2">Nom du Projet</label>
                                            <input
                                                type="text"
                                                value={newProjectName}
                                                onChange={(e) => setNewProjectName(e.target.value)}
                                                placeholder="Mon super film..."
                                                autoFocus
                                                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 rounded-lg text-white placeholder:text-gray-600 focus:border-indigo-500 focus:outline-none text-lg"
                                                onKeyDown={(e) => e.key === 'Enter' && handleNextStep()}
                                            />
                                        </div>
                                        <div className="flex justify-end gap-3 pt-4">
                                            <button
                                                onClick={() => setShowNewProject(false)}
                                                className="px-6 py-2 text-gray-400 hover:text-white"
                                            >
                                                Annuler
                                            </button>
                                            <button
                                                onClick={handleNextStep}
                                                disabled={!newProjectName.trim()}
                                                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Suivant
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {step === 2 && (
                                    <div className="space-y-6">
                                        {/* Local Storage Config */}
                                        <div className="bg-black/30 p-4 rounded-xl border border-gray-700/50">
                                            <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                                                <FolderIcon className="w-4 h-4 text-indigo-400" />
                                                Dossier Local (Proxy Cache)
                                                <div className="group/tooltip relative">
                                                    <div className="w-4 h-4 rounded-full border border-gray-600 text-gray-500 flex items-center justify-center text-[10px] cursor-help hover:text-white hover:border-white transition-colors">?</div>
                                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-800 border border-gray-600 rounded-lg shadow-xl text-xs text-gray-300 opacity-0 group-hover/tooltip:opacity-100 pointer-events-none transition-opacity z-50">
                                                        Emplacement sur votre ordinateur pour les fichiers de travail rapides (base de données locale, miniatures).
                                                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 border-b border-r border-gray-600 rotate-45"></div>
                                                    </div>
                                                </div>
                                            </h4>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={localPath || 'Stockage navigateur (défaut)'}
                                                    readOnly
                                                    className="flex-1 bg-gray-800 text-gray-400 text-sm px-3 py-2 rounded border border-gray-700"
                                                />
                                                <button
                                                    onClick={handleSelectLocalFolder}
                                                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded border border-gray-600"
                                                >
                                                    Choisir...
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Utilisé pour stocker les fichiers légers et le cache.
                                            </p>
                                        </div>

                                        {/* Cloud Storage Config */}
                                        <div className="bg-black/30 p-4 rounded-xl border border-gray-700/50">
                                            <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                                                <span className="text-indigo-400">☁️</span>
                                                Google Drive (Médias HD)
                                                <div className="group/tooltip relative">
                                                    <div className="w-4 h-4 rounded-full border border-gray-600 text-gray-500 flex items-center justify-center text-[10px] cursor-help hover:text-white hover:border-white transition-colors">?</div>
                                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-800 border border-gray-600 rounded-lg shadow-xl text-xs text-gray-300 opacity-0 group-hover/tooltip:opacity-100 pointer-events-none transition-opacity z-50">
                                                        Les fichiers volumineux (vidéos HD) sont stockés sur Drive. Les fichiers légers restent en local pour la rapidité.
                                                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 border-b border-r border-gray-600 rotate-45"></div>
                                                    </div>
                                                </div>
                                            </h4>

                                            {!driveConnected ? (
                                                <div className="text-center py-4">
                                                    <p className="text-sm text-gray-400 mb-3">Connectez Drive pour sauvegarder vos vidéos HD en sécurité.</p>
                                                    {userId ? (
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    const { connectDrive } = await import('../services/googleDriveClient');
                                                                    await connectDrive();
                                                                } catch (error) {
                                                                    console.error('[ProjectWelcome] Drive connect error:', error);
                                                                    alert(`Erreur: ${error instanceof Error ? error.message : 'Connexion Drive échouée'}`);
                                                                }
                                                            }}
                                                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-bold"
                                                        >
                                                            Connecter Google Drive
                                                        </button>
                                                    ) : (
                                                        <p className="text-sm text-yellow-500">⚠️ Connectez-vous d'abord pour activer Drive</p>
                                                    )}
                                                    <p className="text-xs text-yellow-500 mt-2">Requis pour le stockage HD</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    <div className="flex items-center justify-between text-xs text-green-400 mb-2">
                                                        <span>✓ Compte connecté</span>
                                                        <span className="text-gray-500">jenial.app authorized</span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-gray-400 text-xs mb-1">Dossier de destination</label>
                                                        <select
                                                            className="w-full bg-gray-800 text-white text-sm px-3 py-2 rounded border border-gray-700 focus:border-indigo-500 outline-none"
                                                            onChange={(e) => setSelectedDriveFolder(e.target.value)}
                                                            value={selectedDriveFolder || ''}
                                                        >
                                                            <option value="">-- Créer un nouveau dossier --</option>
                                                            {driveFolders.map(f => (
                                                                <option key={f.id} value={f.id}>{f.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex justify-between pt-4">
                                            <button
                                                onClick={() => setStep(1)}
                                                className="px-6 py-2 text-gray-400 hover:text-white"
                                            >
                                                Retour
                                            </button>
                                            <button
                                                onClick={handleCreate}
                                                className="px-8 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold rounded-lg shadow-lg shadow-indigo-900/50"
                                            >
                                                Créer & Configurer
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3">
                                {projects.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-64 text-gray-600">
                                        <FolderIcon className="w-20 h-20 mb-4 opacity-20" />
                                        <p className="text-lg">Aucun projet récent trouvé.</p>
                                        <p className="text-sm">Créez votre premier projet pour commencer l'aventure !</p>
                                    </div>
                                ) : (
                                    projects.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).map((p) => (
                                        <div
                                            key={p.id}
                                            className="group flex items-center justify-between p-4 bg-gray-800/30 hover:bg-gray-800 border border-gray-800 hover:border-gray-600 rounded-xl transition-all cursor-pointer"
                                            onClick={() => handleOpen(p.id)}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center group-hover:bg-indigo-900/50 group-hover:text-indigo-400 transition-colors">
                                                    <FolderIcon className="w-6 h-6 text-gray-500 group-hover:text-indigo-400" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-white text-lg group-hover:text-indigo-300 transition-colors">{p.name}</h3>
                                                    <p className="text-xs text-gray-500">
                                                        Modifié le {new Date(p.updatedAt).toLocaleDateString()} à {new Date(p.updatedAt).toLocaleTimeString()}
                                                    </p>
                                                    {(p.storageMode === 'hybrid' || p.driveFolderId) && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] text-blue-400 bg-blue-900/20 px-1.5 py-0.5 rounded mt-1">
                                                            ☁️ Drive Sync
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpen(p.id);
                                                    }}
                                                    className="px-4 py-2 bg-gray-700 hover:bg-indigo-600 text-white text-sm font-medium rounded-lg transition-colors"
                                                >
                                                    Ouvrir
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(p.id, p.name);
                                                    }}
                                                    className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                                                    title="Supprimer"
                                                >
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jenial"
                    onChange={handleImport}
                    className="hidden"
                />
            </div>
        </div>
    );
};
